// Asking the hoops Functions, and checking what comes back. P6-328b (GN-1b).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IT CANNOT THROW, AND IT DOES NOT SAY WHY IT FAILED
// ═══════════════════════════════════════════════════════════════════════════
//
// `watch.js` and `music.js` resolve every failure to a STATE that renders a
// sentence, and keep a second state for a 500 so that "this deployment is wrong"
// does not wear the sentence for "come back later". The hoops page has no
// sentence to put in either: an unreadable feed draws the tab header and no
// cards, with NO error copy (HP5, and the presence handoff's unreachable rule
// — nothing here is allowed to say a feed is broken, only what it last knew and
// how old that is). So there is one failure, `FAILED`, and the status is not
// carried: a distinction nothing renders is a distinction somebody will one day
// render by accident.
//
// ⚠️ THE OPERATOR HAS THE DISTINCTION ANYWAY. `functions/api/hoops/_failure.js`
// logs which of `HOOPS_FAILURE`'s reasons fired, and the network tab has the status.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE SHAPE IS CHECKED AT THIS SEAM TOO
// ═══════════════════════════════════════════════════════════════════════════
//
// The Function checks what came out of the BUCKET (version, envelope); this
// checks what came over the WIRE, and those are two different edges with a
// proxy, a service worker and a browser cache between them. The checks are the
// ones a component would otherwise crash on, not a schema: an array that is not
// an array, a game that is not the one asked for.
//
// ⚠️ A CARD IT CANNOT RENDER IS LEFT OUT, NOT GUESSED AT. A status this file
// has never heard of would be drawn as one of the three it knows, which is the
// page asserting something the bot did not say.

export const HOOPS_TONIGHT_ENDPOINT = '/api/hoops/tonight'

/** A game id as the NBA writes it: ten digits. The same shape, for the same
 * reason, as the Function's guard — the id reaches a URL and must not be able to
 * reach anything else. */
const NBA_GAME_ID = /^[0-9]{10}$/

/** `value` if it is a ten-digit game id, else `null`. */
export function validGameId(value) {
  return typeof value === 'string' && NBA_GAME_ID.test(value) ? value : null
}

export function hoopsGameEndpoint(gameId) {
  // Validated, not just encoded: encoding would make a bad id a different
  // request, validation makes it no request.
  const id = validGameId(gameId)
  return id === null ? null : `/api/hoops/games/${id}`
}

/** The one failure. See the header for why there is only one. */
export const FAILED = Object.freeze({ ok: false })

export const STATUSES = Object.freeze(['pregame', 'live', 'final'])

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)

const isRenderableCard = (card) => isObject(card) && typeof card.id === 'string' && STATUSES.includes(card.status)

/** The board's answer, checked. `{ ok: true, tonight, teams }` or `FAILED`. */
export function buildTonight(payload) {
  if (!isObject(payload)) return FAILED
  const { tonight, teams } = payload
  if (!isObject(tonight) || !Array.isArray(tonight.games) || !Array.isArray(teams)) return FAILED
  const games = tonight.games.filter(isRenderableCard)
  // `tonight` is passed on whole with only the unrenderable cards removed: a
  // field the producer adds tomorrow (`next_game` was added this way) must
  // reach the component without this file being edited first.
  //
  // P6-343: last night's cards (`last_night.games`) are cards like these, drawn by the same component, so they are held to
  // the same test. A `last_night` that is not an object is passed on as it came and the view draws nothing for it.
  const lastNight = tonight.last_night
  if (isObject(lastNight) && Array.isArray(lastNight.games)) {
    return {
      ok: true,
      tonight: { ...tonight, games, last_night: { ...lastNight, games: lastNight.games.filter(isRenderableCard) } },
      teams,
    }
  }
  return { ok: true, tonight: { ...tonight, games }, teams }
}

/** One game's answer, checked against the id it was asked for. */
export function buildGame(payload, gameId) {
  if (!isObject(payload) || !isObject(payload.game)) return FAILED
  const { game } = payload
  if (game.game_id !== gameId) return FAILED
  if (!STATUSES.includes(game.status)) return FAILED
  if (!isObject(game.home) || !isObject(game.away) || !isObject(game.totals)) return FAILED
  if (!isObject(game.players) || !Array.isArray(game.players.home) || !Array.isArray(game.players.away)) {
    return FAILED
  }
  const plays = Array.isArray(game.plays) ? game.plays : []
  return { ok: true, game: { ...game, plays } }
}

/**
 * One read, answered or `FAILED`, always.
 *
 * Exported (P6-329c) so the season readers (`history.js`) ask the way the board does and not by a second copy of the
 * same policy: the session cookie, the `Accept` header, and a body that is only believed once `build` has checked it.
 */
export async function ask(url, build, fetchImpl) {
  if (typeof fetchImpl !== 'function' || url === null) return FAILED
  try {
    // `credentials: 'same-origin'`: the session cookie is what makes this a 200
    // rather than a 401. 3a is readable by any signed-in member and by nobody
    // else.
    const response = await fetchImpl(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) return FAILED
    return build(await response.json())
  } catch {
    return FAILED
  }
}

/** Ask for the board. Resolves to `{ ok: true, tonight, teams }` or `FAILED`, always. */
export function fetchTonight(fetchImpl = globalThis.fetch) {
  return ask(HOOPS_TONIGHT_ENDPOINT, buildTonight, fetchImpl)
}

/** Ask for one game. Resolves to `{ ok: true, game }` or `FAILED`, always. */
export function fetchGame(gameId, fetchImpl = globalThis.fetch) {
  return ask(hoopsGameEndpoint(gameId), (payload) => buildGame(payload, gameId), fetchImpl)
}

// ═══════════════════════════════════════════════════════════════════════════
// THE FANTASY LAYER (P6-333a, GN-6a): ONE MEMBER'S OWN OBJECT, AND "NOT IN A LEAGUE" IS AN ANSWER
// ═══════════════════════════════════════════════════════════════════════════
//
// `/api/hoops/fantasy` answers a member with their league copy, or a bare 404 for a member who is not linked to one
// (GN2). The 404 is not a failure: it is what the page draws NOTHING fantasy-shaped on, and it is permanent until
// someone links the member, so it is `ABSENT` (asked again in half an hour) and not `FAILED` (asked again soon).
// Everything else that is not a readable blob (a 5xx, a body that is not the shape, an offline tab) is `FAILED`, which
// the hook treats as "draw nothing, and ask again soon". Neither draws a sentence: this layer has no error copy, for
// the board's own reason (HP5, and the presence handoff's unreachable rule).

export const HOOPS_FANTASY_ENDPOINT = '/api/hoops/fantasy'

/** The member has no league copy. A real answer, not a failure. */
export const ABSENT = Object.freeze({ ok: true, absent: true })

/** The blob's answer, checked: only the fields a component would crash on. */
export function buildFantasy(payload) {
  if (!isObject(payload) || !isObject(payload.fantasy)) return FAILED
  const { fantasy } = payload
  if (!isObject(fantasy.league) || !isObject(fantasy.league.scoring) || !Array.isArray(fantasy.league.scoring.stats)) {
    return FAILED
  }
  if (!isObject(fantasy.you) || !Array.isArray(fantasy.you.roster)) return FAILED
  if (!Array.isArray(fantasy.crew)) return FAILED
  return { ok: true, fantasy }
}

/** Ask for the member's own league copy. Resolves to `{ ok: true, fantasy }`, `ABSENT` or `FAILED`, always. */
export async function fetchFantasy(fetchImpl = globalThis.fetch) {
  if (typeof fetchImpl !== 'function') return FAILED
  try {
    const response = await fetchImpl(HOOPS_FANTASY_ENDPOINT, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (response.status === 404) return ABSENT
    if (!response.ok) return FAILED
    return buildFantasy(await response.json())
  } catch {
    return FAILED
  }
}
